import * as React from 'react'
import { Select as AntSelect, ConfigProvider } from 'antd'
import type { SelectProps as AntSelectProps } from 'antd'
import { useInfiniteQuery } from '@tanstack/react-query'

// --- Types ---
export type RemoteInfiniteSelectLoadParams = {
  page: number
  signal?: AbortSignal
  searchText?: string
}

export type RemoteInfiniteSelectOption = {
  value: React.ReactNode
  label: React.ReactNode
}

export type RemoteInfiniteSelectProps = Omit<
  AntSelectProps,
  'onChange' | 'options'
> & {
  cacheKey?: string
  lazy: true
  enableRemoteSearch?: boolean
  searchDebounceMs?: number
  initialPage?: number
  allLabel?: string
  initialOptions?: RemoteInfiniteSelectOption[]
  loadOptions: (params: RemoteInfiniteSelectLoadParams) => Promise<{
    options: RemoteInfiniteSelectOption[]
    hasNextPage?: boolean
    nextPage?: number
  }>
  onLazyValueChange?: (value?: string, option?: any) => void
}

interface LegacySelectProps extends Omit<AntSelectProps, 'onChange'> {
  onValueChange?: (value: any, option?: any) => void
  children?: React.ReactNode
  lazy?: false
}

type CombinedProps = RemoteInfiniteSelectProps | LegacySelectProps

// --- Helpers ---
function useDebouncedValue<T>(value: T, delayMs: number) {
  const [debounced, setDebounced] = React.useState(value)
  React.useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs)
    return () => window.clearTimeout(t)
  }, [value, delayMs])
  return debounced
}

// --- Main Component ---
export const Select = React.forwardRef<any, CombinedProps>((props, ref) => {
  if (props.lazy === true) {
    return <LazySelect {...props} ref={ref} />
  }

  return <LegacySelect {...props} ref={ref} />
})

const LazySelect = React.forwardRef<any, RemoteInfiniteSelectProps>(
  (props, ref) => {
    const {
      loadOptions,
      onLazyValueChange,
      className,
      enableRemoteSearch,
      searchDebounceMs = 300,
      initialPage = 0,
      allLabel,
      cacheKey,
      lazy,
      mode,
      maxTagCount,
      disabled,
      initialOptions,
      ...antdProps
    } = props

    const [searchText, setSearchText] = React.useState('')
    const debouncedSearchText = useDebouncedValue(searchText, searchDebounceMs)

    const query = useInfiniteQuery({
      queryKey: [
        'remote-infinite-select',
        cacheKey,
        loadOptions,
        debouncedSearchText,
      ],
      initialPageParam: initialPage,
      staleTime: 0,
      gcTime: 0,
      queryFn: ({ pageParam, signal }) =>
        loadOptions({
          page: pageParam as number,
          signal,
          searchText: debouncedSearchText,
        }),
      getNextPageParam: (lastPage: any) =>
        lastPage.hasNextPage === false || lastPage.options.length === 0
          ? undefined
          : lastPage.nextPage,
    })

    const remoteOptions = React.useMemo(() => {
      const list = query.data?.pages.flatMap((p: any) => p.options) || []

      const combinedList = [...(props.initialOptions || []), ...list]

      const uniqueMap = new Map()
      combinedList.forEach((item) => {
        if (!uniqueMap.has(item.value)) {
          uniqueMap.set(item.value, item)
        }
      })

      const uniqueList = Array.from(uniqueMap.values())

      if (debouncedSearchText) return uniqueList
      return allLabel
        ? [{ value: 'all', label: allLabel }, ...uniqueList]
        : uniqueList
    }, [query.data, allLabel, debouncedSearchText, initialOptions])

    return (
      <ConfigProvider
        theme={{
          token: {
            colorIcon: '#CE7A58',
            colorTextQuaternary: '#000000',
            colorTextPlaceholder: '#bfbfbf',
          },
          components: {
            Select: {
              colorBgContainer: '#ffffff',
              colorBgElevated: '#ffffff',
              borderRadius: 9999,
            },
          },
        }}
      >
        <AntSelect
          {...antdProps}
          ref={ref}
          virtual
          listHeight={300}
          className={['rounded-full', className].filter(Boolean).join(' ')}
          options={remoteOptions}
          labelInValue={false}
          showSearch={enableRemoteSearch ?? true}
          onSearch={setSearchText}
          filterOption={false}
          mode={mode}
          disabled={disabled}
          maxTagCount={maxTagCount}
          onPopupScroll={(e: React.UIEvent<HTMLDivElement>) => {
            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
            const isNearBottom = scrollHeight - scrollTop <= clientHeight + 200
            if (
              isNearBottom &&
              query.hasNextPage &&
              !query.isFetchingNextPage &&
              !query.isFetching
            ) {
              query.fetchNextPage()
            }
          }}
          onChange={(v, opt) => {
            setSearchText('')
            onLazyValueChange?.(v === 'all' ? undefined : v, opt)
          }}
          popupRender={(menu) => (
            <div className="flex flex-col h-full">
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {query.isFetching && !query.isFetchingNextPage ? (
                  <div className="flex items-center justify-center py-4 text-sm text-gray-500">
                    Đang tìm kiếm...
                  </div>
                ) : (
                  menu
                )}
              </div>
              {query.isFetchingNextPage && (
                <div className="flex items-center justify-center py-2 text-sm text-gray-500">
                  Đang tải thêm...
                </div>
              )}
            </div>
          )}
        />
      </ConfigProvider>
    )
  },
)

// --- Legacy Select ---
const LegacySelect = React.forwardRef<any, LegacySelectProps>((props, ref) => {
  const { onValueChange, children, className, ...rest } = props

  return (
    <ConfigProvider
      theme={{
        token: {
          colorIcon: '#CE7A58',
          colorTextQuaternary: '#000000',
          colorTextPlaceholder: '#bfbfbf',
        },
        components: {
          Select: {
            colorBgContainer: '#ffffff',
            colorBgElevated: '#ffffff',
            borderRadius: 9999,
          },
        },
      }}
    >
      <AntSelect
        {...rest}
        ref={ref}
        className={['rounded-full', className].filter(Boolean).join(' ')}
        onChange={onValueChange}
      >
        {children}
      </AntSelect>
    </ConfigProvider>
  )
})
Select.displayName = 'Select'
